import { en } from "./en";
import type { LocaleId } from "./locales";
import { isLocaleId, matchBrowserLocale, normalizePickerLocale } from "./locales";
import { deepMerge, type DeepPartialMessages } from "./merge";
import { getMessagePath, interpolate } from "./path";
import type { Messages } from "./types";

const STORAGE_KEY = "un8.locale.v1";

type CatalogMap = Record<LocaleId, Messages>;

/** Filled by registerCatalogs() once locale modules load. */
let catalogs: CatalogMap = { en } as CatalogMap;

export function registerCatalogs(map: Partial<CatalogMap> & { en: Messages }): void {
  catalogs = { ...catalogs, ...map, en: map.en };
}

export function getCatalog(locale: LocaleId): Messages {
  return catalogs[locale] || catalogs.en || en;
}

/**
 * Sync first paint: only a saved choice. Browser / geo apply asynchronously
 * in I18nProvider so a Brazilian IP is not German-walled by a DE browser.
 */
export function detectInitialLocale(): LocaleId {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isLocaleId(saved)) return normalizePickerLocale(saved);
  } catch {
    /* ignore */
  }
  return "en";
}

/** True when the visitor already chose (or was assigned) a persisted locale. */
export function hasPersistedLocale(): boolean {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return Boolean(saved && isLocaleId(saved));
  } catch {
    return false;
  }
}

/** Browser language fallback after geo (or when geo is unavailable). */
export function detectBrowserLocale(): LocaleId | null {
  if (typeof navigator === "undefined") return null;
  const candidates = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const tag of candidates) {
    const m = matchBrowserLocale(tag);
    if (m) return normalizePickerLocale(m);
  }
  return null;
}

export function persistLocale(locale: LocaleId): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale === "en-GB" ? "en-GB" : locale;
  }
}

export type Vars = Record<string, string | number>;

export function translate(locale: LocaleId, key: string, vars?: Vars): string {
  const primary = getMessagePath(getCatalog(locale) as unknown as Record<string, unknown>, key);
  const fallback = getMessagePath(en as unknown as Record<string, unknown>, key);
  const raw = primary ?? fallback ?? key;
  return interpolate(raw, vars);
}

export function buildLocale(
  base: Messages,
  overlay: DeepPartialMessages
): Messages {
  return deepMerge(base, overlay);
}

export type { Messages, DeepPartialMessages };
